import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'

import { Button } from '@/components/ui/Button'
import { cmExpertPdfUrl, deleteCmExpertPdf, uploadCmExpertPdf } from '@/api/inspection-leads'
import type { InspectionLead } from '@/types/inspection-lead'
import { dateFnsLocale } from '@/utils/dateLocale'

/**
 * Оценка CM Expert у лида СберАвто (решение Sage 05.11): один PDF, загружает
 * админ/диспетчер на любом этапе, новая замещает старую; Сбер видит файл в
 * «Документах» кабинета. Ссылка на скачивание подписанная, на 10 минут.
 */
export function CmExpertPdfControl({ lead }: { lead: Pick<InspectionLead, 'id' | 'lead_no' | 'cm_expert_pdf_path' | 'cm_expert_pdf_at'> }) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const input = useRef<HTMLInputElement>(null)
  const upload = useMutation({
    mutationFn: (file: File) => uploadCmExpertPdf(lead.id, file),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inspection-lead', lead.id] })
      void qc.invalidateQueries({ queryKey: ['lead-activity', lead.id] })
    },
  })
  const open = useMutation({
    mutationFn: async () => {
      if (!lead.cm_expert_pdf_path) return
      window.location.assign(await cmExpertPdfUrl(lead.cm_expert_pdf_path, lead.lead_no))
    },
  })
  const remove = useMutation({
    mutationFn: () => deleteCmExpertPdf(lead.id, lead.cm_expert_pdf_path as string),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inspection-lead', lead.id] })
      void qc.invalidateQueries({ queryKey: ['lead-activity', lead.id] })
    },
  })
  const error = upload.error ?? open.error ?? remove.error
  return (
    <div className="mt-3 flex flex-col text-left">
      <span className="stryzh-input-label">{t('leads.detail.cmExpert')}</span>
      <div className="mt-1 flex flex-wrap items-center gap-2">
        {lead.cm_expert_pdf_path ? (
          <>
            <Button type="button" variant="ghost" disabled={open.isPending} onClick={() => open.mutate()}>
              {t('leads.detail.cmExpertPdf')}
            </Button>
            {lead.cm_expert_pdf_at ? (
              <span className="text-xs text-[color:var(--stryzh-color-text-secondary)]">
                {t('leads.detail.cmExpertAt', {
                  at: format(new Date(lead.cm_expert_pdf_at), 'd MMM yyyy, HH:mm', { locale: dateFnsLocale() }),
                })}
              </span>
            ) : null}
          </>
        ) : null}
        <Button type="button" variant="ghost" disabled={upload.isPending} onClick={() => input.current?.click()}>
          {upload.isPending
            ? t('leads.detail.cmExpertUploading')
            : lead.cm_expert_pdf_path
              ? t('leads.detail.cmExpertReplace')
              : t('leads.detail.cmExpertUpload')}
        </Button>
        {lead.cm_expert_pdf_path ? (
          <Button
            type="button"
            variant="ghost"
            disabled={remove.isPending}
            onClick={() => {
              if (window.confirm(t('leads.detail.cmExpertDeleteConfirm'))) remove.mutate()
            }}
          >
            {remove.isPending ? t('leads.detail.cmExpertDeleting') : t('leads.detail.cmExpertDelete')}
          </Button>
        ) : null}
        <input
          ref={input}
          type="file"
          accept="application/pdf"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0]
            e.target.value = ''
            if (f) upload.mutate(f)
          }}
        />
      </div>
      {error ? <div className="mt-1 text-xs text-red-300">{(error as Error).message}</div> : null}
    </div>
  )
}
